import type { OHLCV } from '@shared/types/candle'

import { registerIndicator } from '@/indicators/registry'
import type { IndicatorConfig, IndicatorResult } from '@/types/indicator'
import { IndicatorBase } from '@/types/indicator'

const CONFIG: IndicatorConfig = {
  id: 'cpr',
  name: 'CPR (Central Pivot Range)',
  shortName: 'CPR',
  category: 'professional',
  overlay: true,
  params: [],
  lines: [
    { key: 'tc', label: 'Top CPR', color: '#26a69a', width: 1, style: 'dashed' },
    { key: 'pp', label: 'Pivot (PP)', color: '#ffb74d', width: 2 },
    { key: 'bc', label: 'Bottom CPR', color: '#ef5350', width: 1, style: 'dashed' },
  ],
}

/**
 * Build a map of UTC-day → aggregated OHLC for that day.
 * UTC day = floor(t / 86400).  Works well for crypto; close enough for NSE/US.
 */
function buildDailyMap(candles: OHLCV[]): Map<number, { h: number; l: number; c: number }> {
  const m = new Map<number, { h: number; l: number; c: number }>()
  for (const c of candles) {
    const day = Math.floor(c.t / 86400)
    const existing = m.get(day)
    if (!existing) {
      m.set(day, { h: c.h, l: c.l, c: c.c })
    } else {
      if (c.h > existing.h) existing.h = c.h
      if (c.l < existing.l) existing.l = c.l
      existing.c = c.c // last bar of the day is the close
    }
  }
  return m
}

class CPR extends IndicatorBase {
  readonly config = CONFIG

  calculate(candles: OHLCV[]): IndicatorResult {
    const n = candles.length
    const tc: (number | null)[] = new Array<number | null>(n).fill(null)
    const pp: (number | null)[] = new Array<number | null>(n).fill(null)
    const bc: (number | null)[] = new Array<number | null>(n).fill(null)

    if (n < 2)
      return {
        lines: [
          { key: 'tc', values: tc },
          { key: 'pp', values: pp },
          { key: 'bc', values: bc },
        ],
      }

    const dailyMap = buildDailyMap(candles)
    // Sort days ascending so we can find the previous day for any given day
    const sortedDays = [...dailyMap.keys()].sort((a, b) => a - b)
    // Map from each day → previous day's OHLC
    const prevDay = new Map<number, { h: number; l: number; c: number }>()
    for (let i = 1; i < sortedDays.length; i++) {
      prevDay.set(sortedDays[i], dailyMap.get(sortedDays[i - 1])!)
    }

    for (let i = 0; i < n; i++) {
      const day = Math.floor(candles[i].t / 86400)
      const prev = prevDay.get(day)
      if (!prev) continue
      const pivot = (prev.h + prev.l + prev.c) / 3
      const bcVal = (prev.h + prev.l) / 2
      const tcVal = 2 * pivot - bcVal
      pp[i] = pivot
      bc[i] = bcVal
      tc[i] = tcVal
    }

    return {
      lines: [
        { key: 'tc', values: tc },
        { key: 'pp', values: pp },
        { key: 'bc', values: bc },
      ],
    }
  }
}

registerIndicator(CPR, CONFIG)
